"use client"

import * as React from 'react'
import { Loader2, Search, X } from 'lucide-react'
import { Button } from '../../primitives/button'
import { cn } from '@open-mercato/shared/lib/utils'

export type LookupSelectItem = {
  id: string
  title: string
  subtitle?: string | null
  badge?: string | null
  icon?: React.ReactNode
  disabled?: boolean
  rightLabel?: string | null
  description?: string | null
}

type LookupSelectProps = {
  value: string | null
  onChange: (next: string | null) => void
  fetchItems?: (query: string) => Promise<LookupSelectItem[]>
  fetchOptions?: (query?: string) => Promise<LookupSelectItem[]>
  options?: LookupSelectItem[]
  minQuery?: number
  actionSlot?: React.ReactNode
  onReady?: (controls: { setQuery: (value: string) => void }) => void
  searchPlaceholder?: string
  placeholder?: string
  clearLabel?: string
  emptyLabel?: string
  loadingLabel?: string
  selectLabel?: string
  selectedLabel?: string
  minQueryHintLabel?: string
  startTypingLabel?: string
  selectedHintLabel?: (id: string) => string
  disabled?: boolean
  loading?: boolean
  defaultOpen?: boolean
}

export function LookupSelect({
  value,
  onChange,
  fetchItems,
  fetchOptions,
  options,
  minQuery = 2,
  actionSlot,
  onReady,
  placeholder,
  searchPlaceholder = placeholder ?? 'Search…',
  clearLabel = 'Clear selection',
  emptyLabel = 'No results',
  loadingLabel = 'Searching…',
  selectLabel = 'Select',
  selectedLabel = 'Selected',
  minQueryHintLabel,
  startTypingLabel = 'Start typing to search.',
  selectedHintLabel,
  disabled = false,
  loading: loadingProp = false,
  defaultOpen = false,
}: LookupSelectProps) {
  const [query, setQuery] = React.useState('')
  const [items, setItems] = React.useState<LookupSelectItem[]>(options ?? [])
  const [loading, setLoading] = React.useState(false)
  const [hasTyped, setHasTyped] = React.useState(defaultOpen)
  const [error, setError] = React.useState<string | null>(null)
  const fetchItemsRef = React.useRef(fetchItems ?? fetchOptions)
  const setQueryRef = React.useRef(setQuery)

  React.useEffect(() => {
    fetchItemsRef.current = fetchItems ?? fetchOptions
  }, [fetchItems, fetchOptions])

  React.useEffect(() => {
    if (Array.isArray(options)) {
      setItems(options)
    }
  }, [options])

  React.useEffect(() => {
    setQueryRef.current = setQuery
    if (onReady) onReady({ setQuery })
  }, [onReady, setQuery])

  const shouldSearch =
    defaultOpen || query.trim().length >= minQuery || Boolean(value && (options?.length ?? 0) > 0)
  React.useEffect(() => {
    if (disabled) {
      setItems(options ?? [])
      setLoading(false)
      return
    }
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | null = null
    if (!shouldSearch) {
      setItems(options ?? [])
      setLoading(false)
      setError(null)
      return () => { cancelled = true }
    }
    setLoading(true)
    setError(null)
    timer = setTimeout(() => {
      const requestId = Date.now()
      const fetcher = fetchItemsRef.current
      const loader = fetcher ?? (() => Promise.resolve(options ?? []))
      loader(query.trim())
        .then((result) => {
          if (cancelled) return
          setItems(result)
        })
        .catch((err) => {
          if (cancelled) return
          console.error('LookupSelect.fetchItems', err)
          setError('error')
        })
        .finally(() => {
          if (!cancelled) setLoading(false)
        })
      return requestId
    }, 220)
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [query, shouldSearch])

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <input
            className="w-full rounded border pl-8 pr-2 py-2 text-sm"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value)
              setHasTyped(true)
            }}
            placeholder={searchPlaceholder}
            disabled={disabled}
          />
        </div>
        {actionSlot ? <div className="sm:self-start">{actionSlot}</div> : null}
      </div>
      {shouldSearch ? (
        <div className="space-y-2">
          {loading || loadingProp ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              {loadingLabel}
            </div>
          ) : null}
          {!loading && !loadingProp && !items.length ? (
            <p className="text-xs text-muted-foreground">{emptyLabel}</p>
          ) : null}
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {items.map((item) => {
              const isSelected = value === item.id
              const handleSelect = () => {
                if (item.disabled && !isSelected) return
                onChange(item.id)
              }
              return (
                <div
                  key={item.id}
                  className={cn(
                    'flex gap-3 rounded border bg-card p-3 transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                    isSelected ? 'border-primary/70 bg-primary/5' : 'hover:border-primary/50'
                  )}
                  role="button"
                  tabIndex={item.disabled ? -1 : 0}
                  onClick={handleSelect}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      handleSelect()
                    }
                  }}
                  aria-pressed={isSelected}
                >
                  <div className="flex h-10 w-10 sm:h-12 sm:w-12 shrink-0 items-center justify-center overflow-hidden rounded border bg-muted">
                    {item.icon ?? <span className="text-muted-foreground">•</span>}
                  </div>
                  <div className="flex min-w-0 flex-1 flex-col gap-1">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium">{item.title}</div>
                        {item.subtitle ? (
                          <div className="text-xs text-muted-foreground truncate">{item.subtitle}</div>
                        ) : null}
                        {item.description ? (
                          <div className="text-xs text-muted-foreground truncate">{item.description}</div>
                        ) : null}
                      </div>
                      {item.rightLabel ? (
                        <div className="shrink-0 text-xs font-medium text-muted-foreground">{item.rightLabel}</div>
                      ) : null}
                    </div>
                    <div className="flex justify-end">
                      <Button
                        type="button"
                        variant={isSelected ? 'secondary' : 'outline'}
                        size="sm"
                        className="shrink-0"
                        onClick={(event) => {
                          event.stopPropagation()
                          handleSelect()
                        }}
                        disabled={item.disabled && !isSelected}
                      >
                        {isSelected ? selectedLabel : selectLabel}
                      </Button>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
          {value ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="w-fit gap-1 text-sm font-normal"
              onClick={() => onChange(null)}
            >
              <X className="h-4 w-4" />
              {clearLabel}
            </Button>
          ) : null}
        </div>
      ) : hasTyped ? (
        <p className="text-xs text-muted-foreground">
          {minQueryHintLabel ?? `Type at least ${minQuery} characters or paste an id to search.`}
        </p>
      ) : (
        <p className="text-xs text-muted-foreground">{startTypingLabel}</p>
      )}
      {error ? <p className="text-xs text-destructive">{emptyLabel}</p> : null}
    </div>
  )
}
